// src/components/ui/searchBar.js
import React, { useEffect, useRef, useState } from 'react';
import { cn } from '../../lib/utils'; // Path relative to searchBar.js

const SearchBar = ({ 
  placeholder = "Search...",
  value = "",
  onChange,
  onSearch,
  suggestions = [],
  showSuggestions = false,
  onSuggestionClick,
  className = "",
  disabled = false,
  iconType = "dropdown", // "dropdown" or "search"
  ...props 
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [localShowSuggestions, setLocalShowSuggestions] = useState(false);
  const searchInputRef = useRef(null);
  const suggestionsBoxRef = useRef(null);

  // Handle focus state
  const handleFocus = () => {
    setIsFocused(true);
    if (suggestions.length > 0 && value.length > 0) {
      setLocalShowSuggestions(true);
    }
  };

  // Handle blur state
  const handleBlur = () => {
    setIsFocused(false);
    // Delay hiding suggestions to allow clicking on them
    setTimeout(() => {
      setLocalShowSuggestions(false);
    }, 200);
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    onChange?.(e);
    
    // Show suggestions if there are any and input has content
    if (suggestions.length > 0 && newValue.length > 0) {
      setLocalShowSuggestions(true);
    } else {
      setLocalShowSuggestions(false);
    }
  };

  // Handle suggestion click
  const handleSuggestionClick = (suggestion) => {
    onSuggestionClick?.(suggestion);
    setLocalShowSuggestions(false);
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      onSearch?.(value);
      setLocalShowSuggestions(false);
    }
  };

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target) &&
          suggestionsBoxRef.current && !suggestionsBoxRef.current.contains(event.target)) {
        setLocalShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Determine if suggestions should be shown
  const shouldShowSuggestions = showSuggestions !== undefined ? showSuggestions : localShowSuggestions;

  return (
    <div className="relative">
      {/* Search Bar Container */}
      <div 
        data-property-1="Normal" 
        className={cn(
          "w-96 h-14 pl-4 pr-3.5 py-2.5 bg-neutral-50/80 rounded-[10px]",
          "shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20.100000381469727px_0px_rgba(255,255,255,0.35)]",
          "outline outline-1 outline-offset-[-1px] outline-black backdrop-blur-[30px]",
          "border border-gray-300",
          "flex flex-col justify-center items-start gap-2.5",
          "transition-all duration-200",
          isFocused && "ring-2 ring-blue-400 ring-opacity-50",
          disabled && "opacity-50 cursor-not-allowed",
          className
        )}
      >
        <div className="inline-flex justify-start items-center gap-4 w-full">
          {/* Icon - Dropdown or Search */}
          {iconType === "search" ? (
            <svg
              className="w-6 h-6 text-neutral-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          ) : (
            <svg
              className="w-5 h-5 text-neutral-400 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 9l6 6 6-6"
              />
            </svg>
          )}
          
          {/* Search Input */}
          <input
            ref={searchInputRef}
            type="text"
            placeholder={placeholder}
            value={value}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyPress={handleKeyPress}
            disabled={disabled}
            className="flex-1 bg-transparent text-neutral-800 text-lg font-['Figtree'] leading-snug outline-none placeholder-neutral-400 disabled:cursor-not-allowed"
            {...props}
          />
        </div>
      </div>

      {/* Search Suggestions Dropdown */}
      {shouldShowSuggestions && suggestions.length > 0 && (
        <div 
          ref={suggestionsBoxRef}
          className="absolute top-full left-0 w-full bg-white/90 backdrop-blur-md rounded-md shadow-lg mt-2 z-20 overflow-hidden border border-gray-200"
        >
          <ul className="max-h-60 overflow-y-auto">
            {suggestions.map((suggestion, index) => (
              <li key={index}>
                <button
                  type="button"
                  onClick={() => handleSuggestionClick(suggestion)}
                  className="w-full px-4 py-3 text-left text-neutral-800 hover:bg-blue-50 hover:text-blue-600 transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                >
                  {typeof suggestion === 'string' ? suggestion : suggestion.label || suggestion.name || suggestion.id}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default SearchBar;
